import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import viitumMark from '../assets/viitum-mark.png';
import quillauditsBadge from '../assets/quillaudits-badge.png';
import { NETWORK_ENV } from '../config/chains';
import usePublicPresaleStats from '../hooks/usePublicPresaleStats';
import './Landing.css';

// Self-hosted copy of the QuillAudits report; original lives in the
// QuillAudit_smart_contract_audit_Reports repo on GitHub.
const AUDIT_REPORT_URL = '/viitum-audit-quillaudits.pdf';

function formatNumber(value, opts = {}) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, { maximumFractionDigits: opts.maxFrac ?? 0 });
}

function formatPrice(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
}

function CountdownPill({ endDate }) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!endDate) return null;
  const ms = new Date(endDate).getTime() - now;
  if (Number.isNaN(ms) || ms <= 0) return <span className="lp-countdown">Round closed</span>;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return (
    <span className="lp-countdown">
      <span className="lp-countdown-dot" /> Closes in {days}d {hours}h {mins}m
    </span>
  );
}

const NET_LABEL = NETWORK_ENV === 'mainnet' ? 'Mainnet' : 'Testnet';

const CHAINS = [
  { code: 'ETH', name: 'Ethereum', tagline: NET_LABEL, accent: 'eth' },
  { code: 'BSC', name: 'BNB Chain', tagline: NET_LABEL, accent: 'bsc' },
  { code: 'TRON', name: 'TRON', tagline: NET_LABEL, accent: 'tron' },
];

const FEATURES = [
  {
    title: 'AI-driven infrastructure',
    body: '$VIIT powers institutional-grade AI infrastructure and decentralized finance protocols — the foundational layer for high-efficiency digital asset management.',
  },
  {
    title: 'Utility-first, not speculative',
    body: 'The token facilitates low-latency transactions, provides liquidity for automated management tools, and grants access to proprietary AI-driven financial services.',
  },
  {
    title: 'Self-custody, multi-chain',
    body: 'Pay in USDT, USDC, ETH or BNB across Ethereum, BNB Chain and TRON. Connect MetaMask, TronLink or any WalletConnect app — your keys, your tokens.',
  },
  {
    title: 'Built for institutions',
    body: 'Engineered for institutional investors, fintech developers and high-net-worth participants who require scalable, secure, AI-optimized financial tooling.',
  },
];

const STRATEGY = [
  {
    title: 'Fixed, capped supply',
    body: 'Total supply is permanently capped at 100 crore $VIIT to ensure scarcity and prevent inflationary pressure. The full vesting and burn schedule lives in the whitepaper.',
  },
  {
    title: 'Disciplined capital use',
    body: 'Pre-sale capital is strictly allocated to core AI infrastructure development, institutional partnerships, and deep liquidity across Tier-1 exchanges.',
  },
  {
    title: 'Aligned vesting',
    body: 'A tiered vesting schedule for early contributors protects the market from volatility and aligns investor interests with long-term ecosystem growth.',
  },
];

// 100 crore (1,000,000,000) $VIIT — fixed, non-inflationary max supply.
const TOKEN_MAX_SUPPLY = 1_000_000_000;
const TOKEN_ALLOCATIONS = [
  { key: 'utility',   label: 'Public Utility',        pct: 62, color: '#D43B85' },
  { key: 'promoters', label: 'Promoters',             pct: 21, color: '#1FA9D6' },
  { key: 'marketing', label: 'Marketing & Promotion', pct: 15, color: '#F4B740' },
  { key: 'developer', label: 'Developer',             pct: 2,  color: '#4EC8F0' },
];

const tokensFor = (pct) => Math.round((TOKEN_MAX_SUPPLY * pct) / 100);
const fmtInt = (n) => n.toLocaleString('en-US');
// Per-allocation amounts read in millions (620M, 210M, …); only the full
// supply is shown as 1 billion. Trim trailing zeros so 620.00 -> "620".
const millionsFor = (pct) => Number((tokensFor(pct) / 1_000_000).toFixed(2)).toString();

const ROADMAP = [
  {
    phase: 'Phase 01',
    title: 'AI-liquidity engine',
    body: 'Full deployment of the AI-liquidity engine following the close of the final pre-sale stage.',
  },
  {
    phase: 'Phase 02',
    title: 'Fintech integrations',
    body: 'Integration with global fintech partners to bring institutional-grade tooling on-chain.',
  },
  {
    phase: 'Phase 03',
    title: 'Node expansion',
    body: 'Expansion of decentralized node infrastructure to scale throughput and resilience.',
  },
  {
    phase: 'Phase 04',
    title: 'Staking & rewards',
    body: 'Launch of the staking protocol — participants earn rewards for supporting network liquidity.',
  },
];

const TRUST = [
  {
    label: 'Audited',
    title: 'Audited by QuillAudits',
    body: 'The $VIIT smart contracts have been independently audited by QuillAudits, a leading third-party blockchain security firm, to verify code integrity and participant safety. The full report is public.',
  },
  {
    label: 'Leadership',
    title: 'Founder-led delivery',
    body: 'VIITUM is managed by a professional team led by entrepreneur and founder Sagar Sharma, adhering to strict technical and operational standards.',
  },
  {
    label: 'Compliance',
    title: 'Institutional standards',
    body: 'Role-gated treasury, geofenced jurisdictions and a strategic reserve fund underpin consistent utility regardless of market conditions.',
  },
];

const FAQ_GROUPS = [
  {
    category: 'Project Overview',
    items: [
      {
        q: 'What is VIITUM ($VIIT)?',
        a: 'VIITUM is a high-performance financial utility token designed to power institutional-grade AI infrastructure and decentralized finance protocols. It serves as the foundational layer for high-efficiency digital asset management and automated financial systems.',
      },
      {
        q: 'What is the core utility of the $VIIT token?',
        a: 'The $VIIT token functions as the primary utility asset within the ecosystem, facilitating low-latency transactions, providing liquidity for automated management tools, and granting access to proprietary AI-driven financial services.',
      },
      {
        q: 'What market need does VIITUM address?',
        a: 'As global finance transitions toward AI-integrated systems, there is a critical requirement for scalable and secure infrastructure. VIITUM provides the necessary technical framework to support this evolution with precision and reliability.',
      },
    ],
  },
  {
    category: 'Pre-Sale Participation',
    items: [
      {
        q: 'How can I participate in the $VIIT Pre-Sale?',
        a: 'Qualified participants can access the pre-sale exclusively through the official portal at VIITUM.IO. You must connect a secure, decentralized wallet compatible with the platform.',
      },
      {
        q: 'What currencies are accepted for the purchase?',
        a: 'To facilitate global participation, the platform accepts major stablecoins (USDT/USDC) and primary network assets, including ETH and BNB.',
      },
      {
        q: 'Are there purchase limits in place?',
        a: 'Yes. To ensure equitable distribution and long-term project stability, specific tier-based minimum and maximum limits are applied to all participants. Detailed allocation data is available on the Presale Dashboard upon login.',
      },
      {
        q: 'What is the token distribution timeline?',
        a: 'Tokens acquired during the pre-sale phase will be claimable via the VIITUM portal. The claiming period commences following the conclusion of the final pre-sale stage and prior to the initial exchange offering (IEO).',
      },
    ],
  },
  {
    category: 'Tokenomics & Market Strategy',
    items: [
      {
        q: 'What is the total supply of $VIIT?',
        a: 'The total supply is capped at a fixed limit to ensure scarcity and prevent inflationary pressure. Detailed supply figures and the burn schedule are available in the official tokenomics section of the whitepaper.',
      },
      {
        q: 'How are the pre-sale funds being utilized?',
        a: 'Capital raised during the pre-sale is strictly allocated toward core AI infrastructure development, securing institutional partnerships, and ensuring deep liquidity across Tier-1 exchanges.',
      },
      {
        q: 'Is there a vesting or lock-up period for pre-sale participants?',
        a: 'To protect the market from volatility and ensure price stability at launch, a tiered vesting schedule is implemented for early contributors. This aligns investor interests with the long-term growth of the ecosystem.',
      },
      {
        q: 'What makes VIITUM different from other fintech tokens?',
        a: 'Unlike speculative assets, VIITUM is a utility-first token tied directly to AI-driven financial infrastructure. It focuses on high-efficiency transactions and institutional scalability under the leadership of founder Sagar Sharma.',
      },
    ],
  },
  {
    category: 'Security & Compliance',
    items: [
      {
        q: 'Has the smart contract undergone a security audit?',
        a: 'Yes. Security is a primary directive for VIITUM infrastructure. The $VIIT smart contracts have been independently audited by QuillAudits, a leading third-party blockchain security firm, to verify code integrity and participant safety. The full audit report is published publicly.',
      },
      {
        q: 'How is the project governed and managed?',
        a: 'VIITUM is managed by a professional team led by entrepreneur and founder Sagar Sharma. The project adheres to strict technical and operational standards to ensure institutional-grade delivery.',
      },
      {
        q: 'Where can I find detailed technical documentation?',
        a: 'A comprehensive Whitepaper outlining the AI infrastructure, tokenomics, and development roadmap is available for download on the VIITUM.IO homepage.',
      },
    ],
  },
  {
    category: 'Ecosystem & Roadmap',
    items: [
      {
        q: 'What are the primary milestones for 2026?',
        a: 'Following the pre-sale, the primary objectives include the full deployment of the AI-liquidity engine, integration with global fintech partners, and the expansion of decentralized node infrastructure.',
      },
      {
        q: 'Will there be a staking or rewards program?',
        a: 'Yes. To incentivize network security and long-term holding, the VIITUM ecosystem includes a staking protocol where participants can earn rewards for supporting the infrastructure’s liquidity.',
      },
      {
        q: 'How does VIITUM ensure long-term sustainability?',
        a: 'Sustainability is achieved through a revenue-generating utility model. Fees generated within the AI financial tools contribute back to the ecosystem’s development and token stability mechanisms.',
      },
    ],
  },
  {
    category: 'Risk Management',
    items: [
      {
        q: 'How does VIITUM mitigate market volatility?',
        a: 'VIITUM utilizes automated liquidity protocols and a strategic reserve fund to manage market depth and ensure that the utility of the token remains consistent regardless of broader market fluctuations.',
      },
      {
        q: 'Who is the primary audience for the VIITUM ecosystem?',
        a: 'The ecosystem is designed for institutional investors, fintech developers, and high-net-worth individuals who require high-performance, AI-optimized financial tools.',
      },
    ],
  },
  {
    category: 'Technical Support',
    items: [
      {
        q: 'What should I do if I encounter connectivity issues?',
        a: 'Ensure your wallet is configured to the correct network (Ethereum, BNB Chain or TRON) and contains sufficient assets for gas fees. For persistent issues, clear your browser cache or try a different supported browser interface.',
      },
    ],
  },
];

function TokenDonut() {
  const size = 220;
  const stroke = 30;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const gap = 1.4; // px of track shown between segments
  let acc = 0;

  return (
    <div className="lp-tok-donut-wrap">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="lp-tok-donut"
        role="img"
        aria-label="$VIIT token allocation by category"
      >
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
        <g transform={`rotate(-90 ${c} ${c})`}>
          {TOKEN_ALLOCATIONS.map((a) => {
            const len = (a.pct / 100) * circumference;
            const dash = Math.max(len - gap, 0);
            const offset = -((acc / 100) * circumference);
            acc += a.pct;
            return (
              <circle
                key={a.key}
                cx={c}
                cy={c}
                r={r}
                fill="none"
                stroke={a.color}
                strokeWidth={stroke}
                strokeLinecap="butt"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={offset}
                className="lp-tok-seg"
              >
                <title>{`${a.label}: ${millionsFor(a.pct)} M`}</title>
              </circle>
            );
          })}
        </g>
      </svg>
      <div className="lp-tok-donut-center">
        <span className="lp-tok-center-num">1 Billion</span>
        <span className="lp-tok-center-label">Max supply · fixed</span>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { stats, status } = usePublicPresaleStats();

  const priceText = formatPrice(stats.tokenPrice) ?? 'TBA';
  const soldText = formatNumber(stats.tokensSold) ?? '—';
  const totalText = formatNumber(stats.totalTokens) ?? '—';
  const pct = stats.percentSold ?? 0;
  const showCountdown = status === 'ready' && stats.saleEndsAt;

  return (
    <div className="lp-root">
      {/* Background motif */}
      <div className="lp-bg" aria-hidden>
        <div className="lp-bg-grid" />
        <img src={viitumMark} alt="" className="lp-bg-mark" />
      </div>

      {/* Nav */}
      <header className="lp-nav">
        <Link to="/" className="lp-brand">
          <img src={viitumMark} alt="VIITUM" />
          <span>VIITUM</span>
        </Link>
        <nav className="lp-nav-links">
          <a href="#why">Why VIITUM</a>
          <a href="#strategy">Tokenomics</a>
          <a href="#audit">Security</a>
          <a href="#roadmap">Roadmap</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div className="lp-nav-cta">
          <SignedOut>
            <Link to="/sign-in" className="lp-btn lp-btn-ghost">Sign in</Link>
            <Link to="/sign-up" className="lp-btn lp-btn-primary">Get started</Link>
          </SignedOut>
          <SignedIn>
            <Link to="/presale-v2" className="lp-btn lp-btn-primary">Open presale</Link>
          </SignedIn>
        </div>
      </header>

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-hero-eyebrow">
          <span className="lp-pulse" /> Presale live on {NET_LABEL.toLowerCase()}
        </div>
        <h1 className="lp-hero-title">
          The utility layer for<br />
          AI-driven finance.
        </h1>
        <p className="lp-hero-sub">
          VIITUM is a high-performance utility token powering institutional-grade AI
          infrastructure and decentralized finance. Join the $VIIT pre-sale on Ethereum,
          BNB Chain and TRON — pay in the assets you already hold, to a wallet you control.
        </p>

        <div className="lp-hero-actions">
          <SignedOut>
            <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={() => navigate('/sign-up')}>
              Join the presale →
            </button>
            <button className="lp-btn lp-btn-ghost lp-btn-lg" onClick={() => navigate('/sign-in')}>
              I already have an account
            </button>
          </SignedOut>
          <SignedIn>
            <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={() => navigate('/presale-v2')}>
              Go to presale →
            </button>
          </SignedIn>
        </div>

        {/* Live presale strip */}
        <div className="lp-stat-strip" id="presale">
          <div className="lp-stat">
            <span className="lp-stat-label">Price</span>
            <span className="lp-stat-value">{priceText}</span>
          </div>
          <div className="lp-stat lp-stat-wide">
            <span className="lp-stat-label">Tokens sold</span>
            <span className="lp-stat-value">
              {soldText} <span className="lp-stat-of">/ {totalText}</span>
            </span>
            <div className="lp-progress" aria-label={`${pct.toFixed?.(1) ?? pct}% sold`}>
              <div className="lp-progress-fill" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
            </div>
          </div>
          <div className="lp-stat">
            <span className="lp-stat-label">Status</span>
            <span className="lp-stat-value">
              {status === 'loading' ? 'Loading…' : stats.isPresaleActive ? 'Active' : 'Standby'}
            </span>
            {showCountdown && <CountdownPill endDate={stats.saleEndsAt} />}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="lp-section" id="why">
        <div className="lp-section-head">
          <span className="lp-kicker">Why VIITUM</span>
          <h2>Infrastructure, not speculation.</h2>
        </div>
        <div className="lp-feature-grid">
          {FEATURES.map((f) => (
            <article key={f.title} className="lp-feature">
              <div className="lp-feature-rule" />
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Tokenomics & strategy */}
      <section className="lp-section lp-section-alt" id="strategy">
        <div className="lp-section-head">
          <span className="lp-kicker">Tokenomics</span>
          <h2>A fixed supply, transparently allocated.</h2>
        </div>

        <div className="lp-tok-panel">
          <TokenDonut />
          <div className="lp-tok-legend">
            {TOKEN_ALLOCATIONS.map((a) => (
              <div key={a.key} className="lp-tok-row">
                <span className="lp-tok-swatch" style={{ background: a.color }} />
                <div className="lp-tok-row-main">
                  <div className="lp-tok-row-top">
                    <span className="lp-tok-name">{a.label}</span>
                    <span className="lp-tok-pct">{millionsFor(a.pct)} M</span>
                  </div>
                  <div className="lp-tok-bar">
                    <div
                      className="lp-tok-bar-fill"
                      style={{ width: `${a.pct}%`, background: a.color }}
                    />
                  </div>
                  <span className="lp-tok-amount">
                    {fmtInt(tokensFor(a.pct))} VIIT <span className="lp-tok-cr">· {millionsFor(a.pct)} million</span>
                  </span>
                </div>
              </div>
            ))}
            <div className="lp-tok-total">
              <span>Total max supply</span>
              <span>
                {fmtInt(TOKEN_MAX_SUPPLY)} VIIT <em>· 1 Billion · fixed</em>
              </span>
            </div>
          </div>
        </div>

        <div className="lp-strategy-grid">
          {STRATEGY.map((s) => (
            <article key={s.title} className="lp-strategy">
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Chains */}
      <section className="lp-section" id="chains">
        <div className="lp-section-head">
          <span className="lp-kicker">Three chains, one token</span>
          <h2>Pay on the rails you already trust.</h2>
        </div>
        <div className="lp-chain-grid">
          {CHAINS.map((c) => (
            <div key={c.code} className={`lp-chain lp-chain-${c.accent}`}>
              <div className="lp-chain-code">{c.code}</div>
              <div className="lp-chain-name">{c.name}</div>
              <div className="lp-chain-tag">{c.tagline}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="lp-section lp-section-alt" id="roadmap">
        <div className="lp-section-head">
          <span className="lp-kicker">Roadmap 2026</span>
          <h2>What comes after the pre-sale.</h2>
        </div>
        <ol className="lp-roadmap">
          {ROADMAP.map((r) => (
            <li key={r.phase} className="lp-roadmap-item">
              <div className="lp-roadmap-node" />
              <span className="lp-roadmap-phase">{r.phase}</span>
              <h3>{r.title}</h3>
              <p>{r.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Security audit — QuillAudits */}
      <section className="lp-section" id="audit">
        <div className="lp-audit-card">
          <div className="lp-audit-glow" aria-hidden />
          <div className="lp-audit-badge">
            <a
              href={AUDIT_REPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open the VIITUM smart contract audit report by QuillAudits"
            >
              <img src={quillauditsBadge} alt="Audited by QuillAudits" />
            </a>
          </div>
          <div className="lp-audit-body">
            <span className="lp-kicker">Security audit</span>
            <h2>The $VIIT contracts have been independently audited.</h2>
            <p>
              QuillAudits — a leading smart-contract security firm — completed a full
              source-code review of the VIITUM contracts across Ethereum, BNB Chain and
              TRON. The complete report is published and open for anyone to verify.
            </p>
            <div className="lp-audit-actions">
              <a
                href={AUDIT_REPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-btn lp-btn-primary"
              >
                Read the full report →
              </a>
              <a
                href="https://github.com/Quillhash/QuillAudit_smart_contract_audit_Reports/blob/master/VIITUM%20Smart%20Contract%20Audit%20Report%20-%20QuillAudits.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="lp-btn lp-btn-ghost"
              >
                Verify on GitHub
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & governance */}
      <section className="lp-section" id="trust">
        <div className="lp-section-head">
          <span className="lp-kicker">Security &amp; Governance</span>
          <h2>Institutional-grade by directive.</h2>
        </div>
        <div className="lp-trust-grid">
          {TRUST.map((t) => (
            <article key={t.title} className="lp-trust">
              <span className="lp-trust-label">{t.label}</span>
              <h3>{t.title}</h3>
              <p>{t.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="lp-section lp-section-alt" id="faq">
        <div className="lp-section-head">
          <span className="lp-kicker">FAQ</span>
          <h2>Questions, answered.</h2>
        </div>
        <div className="lp-faq-groups">
          {FAQ_GROUPS.map((group) => (
            <div key={group.category} className="lp-faq-group">
              <h3 className="lp-faq-category">{group.category}</h3>
              <div className="lp-faq">
                {group.items.map((item, i) => (
                  <details key={i} className="lp-faq-item">
                    <summary>{item.q}</summary>
                    <p>{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="lp-cta">
        <h2>Get in before the round closes.</h2>
        <p>Create an account in under a minute and connect your wallet.</p>
        <SignedOut>
          <Link to="/sign-up" className="lp-btn lp-btn-primary lp-btn-lg">Get started →</Link>
        </SignedOut>
        <SignedIn>
          <Link to="/presale-v2" className="lp-btn lp-btn-primary lp-btn-lg">Open presale →</Link>
        </SignedIn>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-brand">
            <img src={viitumMark} alt="" />
            <span>VIITUM</span>
          </div>
          <p>© {new Date().getFullYear()} VIITUM. {NET_LABEL} — nothing on this page is financial advice.</p>
        </div>
      </footer>
    </div>
  );
}
